import {all, takeLatest, put, takeLeading, call, select} from 'redux-saga/effects';
/* Helpers */
import {
  parseDashboard,
  mapDashboardData,
  updateFavoriteDashboardsOrder,
  getLatestViewInDashboards,
  prepareDashboardDataForExport,
} from '../../helpers/dashboard-helpers';
/* Actions */
import {
  ActionTypes,
  getSchedulesSuccess,
  getFutureSchedulesSuccess,
  getPastSchedulesSuccess,
  getSchedulesError,
} from './dashboard-actions';
/* Services */
import DashboardService from '../../services/dashboard-service';
import {LATEST_VIEW_DASHBOARD_LIBRARY} from '../../helpers/constants';
import {exportJson} from '../../helpers/util';
import {getAllDashboardsFromState, getWidgetTypeFromState} from './dashboard-selectors';
import {error, success} from '../../helpers/notifications';

function* getImageOptions() {
  const imageOptions = yield DashboardService.getImageOptions();
  yield put({
    type: ActionTypes.DASHBOARD_SET_IMAGE_OPTIONS,
    imageOptions: imageOptions.getImageOptions(),
    defaultImage: imageOptions.getDefaultImage(),
  });
}

function* getPermissionOptions() {
  const permissionOptions = yield DashboardService.getPermissionSettingOptions();
  yield put({
    type: ActionTypes.DASHBOARD_SET_PERMISSION_OPTIONS,
    permissionOptions,
  });
}

function* getWidgetTypes(action) {
  const query = action.query || null;
  const widgetTypes = yield DashboardService.getWidgetTypes(query);
  yield put({
    type: ActionTypes.DASHBOARD_SET_WIDGET_TYPES,
    widgetTypes,
  });
}

function* publishDashboard({history, id, dashboard, queryParams, widgetTypes}) {
  yield put({
    type: ActionTypes.DASHBOARD_SET_PUBLISHING_STATUS,
    publishingDashboard: true,
  });
  const dashboardMapped = mapDashboardData(dashboard);
  const dashboardResponse = yield DashboardService.publish(dashboardMapped, id, false, widgetTypes);
  yield put({
    type: ActionTypes.DASHBOARD_SET_PUBLISHING_STATUS,
    publishingDashboard: false,
  });
  const latestView = getLatestViewInDashboards();
  const redirectUrl =
    !latestView || latestView === LATEST_VIEW_DASHBOARD_LIBRARY
      ? '/dashboard/library'
      : `/dashboard/view/${id}`;
  if (!dashboardResponse) {
    return;
  }
  if (!id) {
    yield put({
      type: ActionTypes.SET_HIGHLIGHTED_DASHBOARD,
      dashboardName: dashboardMapped.dashboardName,
    });
  }
  yield put({
    type: ActionTypes.DASHBOARD_GET_ALL,
    queryParams,
  });
  history.push(redirectUrl);
}

function* cloneDashboard({data, history, queryParams}) {
  const mappedCloneDashboardData = mapDashboardData(data);
  const dashboardResponse = yield DashboardService.publish(mappedCloneDashboardData, null, true);
  if (dashboardResponse) {
    yield put({
      type: ActionTypes.SET_HIGHLIGHTED_DASHBOARD,
      dashboardName: mappedCloneDashboardData.dashboardName,
    });
    yield put({type: ActionTypes.DASHBOARD_GET_ALL, fetching: false, queryParams});
    history.push('/dashboard/library');
  }
}

function* getAllDevices() {
  const feedData = yield DashboardService.getDevices();
  yield put({type: ActionTypes.DASHBOARD_GET_ALL_DEVICES_SUCCESS, feedData});
}

function* getAll({fetching, queryParams = null, willFetchAfterDelete}) {
  yield put({
    type: ActionTypes.FETCHING_DASHBOARDS,
    fetching,
  });
  const [dashboardsData, widgetTags] = yield all([
    DashboardService.getAll(queryParams),
    DashboardService.fetchTags(),
  ]);
  const dashboards = dashboardsData.map((dashboard) => parseDashboard(dashboard, null, widgetTags));
  const otherDashboards = [];
  const favoriteDashboards = [];
  dashboards.forEach((dashboard) => {
    if (dashboard.modalFormData.isFavorite) {
      favoriteDashboards.push(dashboard);
    } else {
      otherDashboards.push(dashboard);
    }
  });
  otherDashboards.sort((a, b) => {
    const aPubDate = new Date(a.createdOn).getTime();
    const bPubDate = new Date(b.createdOn).getTime();
    return aPubDate >= bPubDate ? -1 : 1;
  });
  yield put({
    type: ActionTypes.DASHBOARD_SET_ALL,
    dashboards,
    otherDashboards,
    favoriteDashboards,
    availableTags: widgetTags,
  });
  if (willFetchAfterDelete) {
    success({message: 'Dashboard successfully deleted'});
  }
}

function* deleteDashboard(action) {
  const response = yield DashboardService.delete(action.dashboard);
  const willFetchAfterDelete = true;
  if (response) {
    yield put({
      type: ActionTypes.DASHBOARD_GET_ALL,
      fetching: false,
      queryParams: action.query,
      willFetchAfterDelete,
    });
  }
}

function* addDashboardToFavorite(action) {
  yield DashboardService.addToFavorites(action.dashboard);
  yield put({
    type: ActionTypes.DASHBOARD_GET_ALL,
    fetching: false,
    queryParams: action.queryParams,
  });
}

function* removeDashboardFromFavorites(action) {
  yield DashboardService.removeFromFavorite(action.dashboard);
  yield put({
    type: ActionTypes.DASHBOARD_GET_ALL,
    fetching: false,
    queryParams: action.queryParams,
  });
}

function* getSchedulesStart(action) {
  try {
    const scheduleFeed = yield DashboardService.getSchedulesforCalendar(action.payload);
    yield put(getSchedulesSuccess(scheduleFeed));
  } catch (err) {
    yield put(getSchedulesError(err));
  }
}

function* getFutureSchedulesStart(action) {
  try {
    const triggerTypes = action.payload.map((triggerType) => triggerType.getId());
    const futureSchedulesFeed = yield DashboardService.getFutureSchedules(triggerTypes);
    yield put(getFutureSchedulesSuccess(futureSchedulesFeed));
  } catch (err) {
    yield put(getSchedulesError(err));
  }
}

function* getPastSchedulesStart(action) {
  try {
    const triggerTypes = action.payload.map((triggerType) => triggerType.getId());
    const pastSchedulesFeed = yield DashboardService.getPastScheduleTriggers(triggerTypes);
    yield put(getPastSchedulesSuccess(pastSchedulesFeed));
  } catch (err) {
    yield put(getSchedulesError(err));
  }
}

function* getOsPaths() {
  let paths = {};
  try {
    paths = yield DashboardService.loadOsPathsForGridWidget();
  } catch (err) {
    paths = {};
  }
  yield put({
    type: ActionTypes.DASHBOARD_SET_OS_PATHS,
    paths,
  });
}

function* getOsMap() {
  let osMap = {};
  try {
    osMap = yield DashboardService.loadOsMapForGridWidget();
  } catch (err) {
    osMap = {};
  }
  yield put({
    type: ActionTypes.DASHBOARD_SET_OS_MAP,
    osMap,
  });
}

function* reorderFavoriteDashboards({dashboards}) {
  updateFavoriteDashboardsOrder(dashboards, 'REORDER');
  yield put({
    type: ActionTypes.SET_FAVORITE_DASHBOARDS,
    dashboards,
  });
}

function* exportDashboards({dashboards}) {
  let exportData = dashboards.map((dashboard) => prepareDashboardDataForExport(dashboard));

  if (!exportData || !exportData.length) {
    error({name: 'Error: ', message: 'Error: Dashboards export failed'});
    return;
  }

  const onlyOneDashboardName = exportData.length === 1 ? `_${exportData[0].name}` : '';
  const name = DashboardService.generateExportFileName(onlyOneDashboardName);
  exportJson(exportData, name);

  const successMessage = `${
    exportData.length === 1
      ? 'The dashboard you exported is now'
      : `The dashboards you exported are now`
  } in your Downloads folder.`;

  success({name: 'Success! ', message: successMessage});
  yield put({
    type: ActionTypes.REMOVE_ALL_DASHBOARDS_FOR_EXPORT,
  });
}

function* importDashboards({dashboards}) {
  const allDashboards = yield select(getAllDashboardsFromState);
  dashboards = DashboardService.adjustDashboardsImportNames(dashboards, allDashboards);

  yield all(dashboards.map((dashboard) => call(importDashboard, dashboard)));
  yield put({type: ActionTypes.DASHBOARD_GET_ALL, fetching: true});
}

function* importDashboard(dashboard) {
  const widgetTypes = yield select(getWidgetTypeFromState);

  const mappedDashboardData = yield DashboardService.adjustDashboardWidgetsForImport(
    dashboard,
    widgetTypes
  );
  const isWarning =
    Object.keys(mappedDashboardData.widgets).length !== Object.keys(dashboard.widgets).length;

  yield DashboardService.importDashboard(mappedDashboardData, isWarning);
}

export default function* dashboardSaga() {
  yield all([
    takeLatest(ActionTypes.DASHBOARD_GET_IMAGE_OPTIONS, getImageOptions),
    takeLatest(ActionTypes.DASHBOARD_GET_PERMISSION_OPTIONS, getPermissionOptions),
    takeLatest(ActionTypes.DASHBOARD_GET_WIDGET_TYPES, getWidgetTypes),
    takeLatest(ActionTypes.DASHBOARD_PUBLISH, publishDashboard),
    takeLatest(ActionTypes.DASHBOARD_DELETE, deleteDashboard),
    takeLeading(ActionTypes.DASHBOARD_GET_ALL, getAll),
    takeLatest(ActionTypes.DASHBOARD_GET_ALL_DEVICES, getAllDevices),
    takeLatest(ActionTypes.DASHBOARD_ADD_TO_FAVORITE, addDashboardToFavorite),
    takeLatest(ActionTypes.DASHBOARD_REMOVE_FROM_FAVORITES, removeDashboardFromFavorites),
    takeLatest(ActionTypes.DASHBOARD_GET_SCHEDULES_START, getSchedulesStart),
    takeLatest(ActionTypes.DASHBOARD_GET_FUTURE_SCHEDULES_START, getFutureSchedulesStart),
    takeLatest(ActionTypes.DASHBOARD_GET_PAST_SCHEDULES_START, getPastSchedulesStart),
    takeLatest(ActionTypes.DASHBOARD_GET_OS_PATHS, getOsPaths),
    takeLatest(ActionTypes.DASHBOARD_GET_OS_MAP, getOsMap),
    takeLatest(ActionTypes.DASHBOARD_CLONE, cloneDashboard),
    takeLatest(ActionTypes.REORDER_FAVORITE_DASHBOARDS, reorderFavoriteDashboards),
    takeLatest(ActionTypes.DASHBOARDS_IMPORT, importDashboards),
    takeLatest(ActionTypes.DASHBOARDS_EXPORT, exportDashboards),
  ]);
}
